import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, MessageSquare, BookOpen, Download, Newspaper, Tag, ChevronRight, CheckCircle } from 'lucide-react';
import { blogList } from '../api';

const STATS    = [['500+','Legal Documents'],['24','Law Categories'],['1,200+','Active Users'],['KH/EN','Languages']];
const FEATURES = [
  [Search,        'Smart Document Search', 'Search all Cambodian legal documents by keyword, category, or year.'],
  [MessageSquare, 'AI Legal Assistant',    'Ask questions in plain language. AI cites exact document names and page numbers.'],
  [BookOpen,      'Personal Collection',   'Save documents and create custom tags for your organized legal library.'],
  [Download,      'PDF Downloads',         'Download any law document as PDF. Available to all subscribers.'],
  [Newspaper,     'News & Articles',       'Stay updated with the latest Cambodian law news, guides, and insights.'],
  [Tag,           'Custom Tags',           'Organize saved documents with your own tags by client, case type, or priority.'],
];

export default function Landing() {
  const [q, setQ]         = useState('');
  const [arts, setArts]   = useState([]);
  const navigate = useNavigate();

  useEffect(() => { blogList().then(r => setArts(r.data.articles?.slice(0,3)||[])).catch(()=>{}); }, []);

  const goSearch = () => { if (q.trim()) navigate(`/search?q=${encodeURIComponent(q)}`); };

  return (
    <div>
      {/* Hero */}
      <div className="bg-gradient-to-br from-blue-950 via-blue-900 to-blue-800 text-white py-20 px-6 text-center">
        <span className="inline-block bg-yellow-500 text-blue-950 text-xs font-bold px-3 py-1 rounded-full mb-5">🇰🇭 Cambodia's Legal Research Platform</span>
        <h1 className="text-4xl md:text-5xl font-bold mb-4 max-w-3xl mx-auto leading-tight">
          Law of Cambodia — <span className="text-yellow-400">Your Legal</span> Research Library
        </h1>
        <p className="text-blue-200 text-base mb-8 max-w-xl mx-auto">Search legal documents, ask AI questions, and build your personal law collection.</p>
        <div className="flex flex-wrap gap-3 justify-center mb-10">
          <Link to="/search" className="bg-yellow-500 text-blue-950 px-6 py-3 rounded-xl font-bold hover:bg-yellow-400 flex items-center gap-2 transition"><Search size={16}/>Start Searching</Link>
          <Link to="/pricing" className="border border-white text-white px-6 py-3 rounded-xl font-semibold hover:bg-white hover:text-blue-950 transition">View Pricing</Link>
        </div>
        <div className="flex max-w-lg mx-auto">
          <input value={q} onChange={e=>setQ(e.target.value)} onKeyDown={e=>e.key==='Enter'&&goSearch()} placeholder="Search Cambodian law documents..." className="flex-1 px-4 py-3 rounded-l-xl text-gray-800 outline-none text-sm"/>
          <button onClick={goSearch} className="bg-yellow-500 text-blue-950 px-5 rounded-r-xl font-bold hover:bg-yellow-400 transition"><Search size={18}/></button>
        </div>
      </div>

      {/* Stats */}
      <div className="bg-white border-b py-8 px-6">
        <div className="max-w-4xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {STATS.map(([v,l],i)=><div key={i}><div className="text-2xl font-bold text-blue-900">{v}</div><div className="text-sm text-gray-400 mt-1">{l}</div></div>)}
        </div>
      </div>

      {/* Features */}
      <div className="bg-gray-50 py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-blue-950 mb-12">Everything You Need for Legal Research</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {FEATURES.map(([Icon,title,desc],i)=>(
              <div key={i} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                <div className="text-yellow-500 mb-3"><Icon size={26}/></div>
                <h3 className="font-bold text-gray-800 mb-2">{title}</h3>
                <p className="text-gray-500 text-sm">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Latest Articles */}
      {arts.length>0&&(
        <div className="bg-white py-16 px-6">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-bold text-blue-950">Latest News & Articles</h2>
              <Link to="/blog" className="text-blue-600 text-sm font-semibold hover:text-blue-800 flex items-center gap-1">View All<ChevronRight size={14}/></Link>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {arts.map(a=>(
                <Link key={a.id} to={`/blog/${a.slug}`} className="bg-gray-50 rounded-2xl p-5 hover:shadow-md transition border border-gray-100 block">
                  <div className="text-3xl mb-3">{a.emoji||'📰'}</div>
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">{a.category}</span>
                  <h3 className="font-bold text-gray-800 mt-2 mb-2 text-sm leading-snug">{a.title}</h3>
                  <p className="text-xs text-gray-500 line-clamp-2">{a.excerpt}</p>
                  <p className="text-xs text-gray-400 mt-3">{a.published_at?new Date(a.published_at).toLocaleDateString():''}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Pricing CTA */}
      <div className="bg-blue-950 text-white py-16 px-6 text-center">
        <h2 className="text-3xl font-bold mb-2">Simple Annual Pricing</h2>
        <p className="text-blue-300 mb-8">Full access to all documents, AI chat, and downloads</p>
        <div className="inline-block bg-white text-blue-950 rounded-2xl p-8 shadow-xl text-left max-w-xs w-full">
          <div className="text-xs text-yellow-600 font-bold mb-1">ANNUAL SUBSCRIPTION</div>
          <div className="text-5xl font-bold mb-1">$49<span className="text-xl text-gray-400 font-normal">/year</span></div>
          <div className="text-gray-400 text-sm mb-6">Pay once, access all year</div>
          {['Unlimited document access','PDF downloads','AI legal assistant','Personal collection & tags','Priority support'].map((f,i)=>(
            <div key={i} className="flex items-center gap-2 text-sm mb-2"><CheckCircle size={15} className="text-green-500"/>{f}</div>
          ))}
          <Link to="/pricing" className="mt-4 block text-center w-full bg-yellow-500 text-blue-950 py-3 rounded-xl font-bold hover:bg-yellow-400 transition">
            Subscribe — Pay with ABA Bank
          </Link>
        </div>
      </div>
    </div>
  );
}
